import { useMemo } from 'react'
import { type ElasticFilter, type ElasticFiltersInput, type ElasticFiltersSuccess, HOUR } from '@terros/common'
import { keepPreviousData, useQueryClient } from '@tanstack/react-query'
import { useTantalimQuery } from '../query'
import { type GetHookResponse } from '../hooks'
import { searchAccountsKey } from './keys'
import { findCachedDataForRegion, queryObjectToKey } from './findCachedDataForRegion'

export function useElasticFilters(
  input: ElasticFiltersInput,
  enabled: boolean = true
): GetHookResponse<ElasticFiltersSuccess> {
  const queryClient = useQueryClient()
  const cachedData = useMemo(() => {
    if (!enabled) return undefined
    return findCachedDataForRegion(queryClient, input)
  }, [queryClient, input, enabled])

  const query = useTantalimQuery<ElasticFiltersInput, ElasticFiltersSuccess, ElasticFiltersSuccess>({
    url: '/search/filters',
    queryKey: queryObjectToKey(input),
    input,
    convertResponse: (data) => data,
    options: {
      gcTime: HOUR,
      staleTime: HOUR,
      enabled: enabled && cachedData === undefined,
      placeholderData: keepPreviousData,
    },
  })

  return useMemo(() => {
    if (cachedData) {
      return {
        ...query,
        data: cachedData,
        isSuccess: true,
        isLoading: false,
        error: undefined,
        refetch: () => queryClient.invalidateQueries({ queryKey: searchAccountsKey() }),
      }
    }
    return query
  }, [query, cachedData, queryClient])
}

export const US_BOUNDING_BOX: ElasticFilter['boundingBox'] = {
  top: { latitude: 71.3904, longitude: -179.148909 },
  bottom: { latitude: -14.374, longitude: 179.77847 },
}
